import {AfterViewInit, Component, OnInit} from '@angular/core';
import ContextBarService from "../context-bar.service";
import {BehaviorSubject, combineLatest, Observable, of, switchMap} from "rxjs";
import {CalendarEvent} from "../../mini-calendar/mini-calendar.component";
import dayjs from "dayjs";
import CooperationEventModel from "../../../../model/cooperationEvent.model";
import CalendarDayModel from "../../../../model/calendarDay.model";
import CalendarService from "../../../../api/calendar/calendar.service";
import {debounceTime, distinctUntilChanged, first} from "rxjs/operators";
import EventService from "../../../../api/event/event.service";
import CooperationModel from "../../../../model/cooperation.model";
import {EventFormComponent, EventFormDialogModel} from "../../event/event-form/event-form.component";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {
    CooperationDialogComponent,
    CooperationDialogModel
} from "../../../dialogs/cooperation-dialog/cooperation-dialog.component";
import {Router} from "@angular/router";

@Component({
  selector: 'context-bar-events',
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss']
})
export class EventsComponent implements OnInit, AfterViewInit {
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private selectedMonthEventsSubject = new BehaviorSubject<CalendarEvent[]>([]);
  public selectedMonthEvents$ = this.selectedMonthEventsSubject.asObservable();

  private selectedCalendarMonthSubject = new BehaviorSubject<Date>(new Date());
  public selectedCalendarMonth$ = this.selectedCalendarMonthSubject.asObservable();

  private selectedCalendarDateSubject = new BehaviorSubject<Date>(null);
  public selectedCalendarDate$ = this.selectedCalendarDateSubject.asObservable();

  private selectedCalendarDateEventsSubject = new BehaviorSubject<CooperationEventModel[]>([]);
  public selectedCalendarDateEvents$ = this.selectedCalendarDateEventsSubject.asObservable();

  private modeSubject = new BehaviorSubject<'calendar'|'list'>('list');
  public mode$ = this.modeSubject.asObservable();

  private eventListSubject = new BehaviorSubject<CooperationEventModel[]>([]);
  public eventList$ = this.eventListSubject.asObservable();

  public cooperation$: Observable<CooperationModel>;

  public constructor(private contextBarService: ContextBarService, private calendarService: CalendarService, private eventService: EventService,
                     private dialog: MatDialog, private snackBar: MatSnackBar, private router: Router) {
  }

  ngOnInit() {
    this.contextBarService.cooperation$.subscribe(cooperation => {
        if(cooperation) {
            this.modeSubject.next('list');
        }
    });
    this.cooperation$ = this.contextBarService.cooperation$;
  }

  ngAfterViewInit() {
    combineLatest([
        this.selectedCalendarMonth$.pipe(distinctUntilChanged()),
        this.contextBarService.artist$,
        this.contextBarService.cooperation$,
        this.contextBarService.customer$,
        this.mode$
    ]).pipe(debounceTime(500))
        .pipe(switchMap(([month, artist, cooperation, customer, mode]) => {
          let additional = {};
          if(artist) {
            additional['artist'] = artist.id;
          }
          if(customer) {
            additional['customer'] = customer.id;
          }
          if(cooperation) {
            additional['cooperation'] = cooperation.id;
          }


          if(mode == 'list') {
              return of([]);
          }
          return this.calendarService.month(month as any, additional).pipe(first());
    })).subscribe(result => {
        this.selectedMonthEventsSubject.next(result.map(e => {
            return { date: e.date, type: 'event' } as CalendarEvent;
        }));
    });

    combineLatest([
          this.selectedCalendarDate$.pipe(distinctUntilChanged()),
          this.contextBarService.artist$,
          this.contextBarService.cooperation$,
          this.contextBarService.customer$
      ]).pipe(distinctUntilChanged(), debounceTime(500)).subscribe(([day, artist, cooperation, customer]) => {
        if(!day) {
          return of([]);
        }
        let additional = {};
        if(artist) {
            additional['artist'] = artist.id;
        }
        if(customer) {
            additional['customer'] = customer.id;
        }
        if(cooperation) {
            additional['cooperation'] = cooperation.id;
        }
        const utcDate = new Date(day.getTime() - day.getTimezoneOffset() * 60000);

        additional['from'] = dayjs(utcDate.toISOString()).set('hour', 12).set('minute', 0).set('second', 0).toDate().toISOString();
        additional['to'] = dayjs(utcDate.toISOString()).set('hour', 12).set('minute', 0).set('second', 0).toDate().toISOString();
        additional['show_all'] = true;

        this.loadingSubject.next(true);
        this.eventService.list(null, 1, 50, null, null, additional)
            .subscribe(result => {
                this.selectedCalendarDateEventsSubject.next(result.results);
                if(!result.cachedVersion) {
                    this.loadingSubject.next(false);
                }
            });
      });

      combineLatest([
          this.contextBarService.artist$,
          this.contextBarService.cooperation$,
          this.contextBarService.customer$,
          this.mode$
      ]).pipe(distinctUntilChanged(), debounceTime(500)).subscribe(([artist, cooperation, customer, mode]) => {
          if(mode != 'list') {
              return of([]);
          }
          let additional = {};
          if(artist) {
              additional['artist'] = artist.id;
          }
          if(customer) {
              additional['customer'] = customer.id;
          }
          if(cooperation) {
              additional['cooperation'] = cooperation.id;
              additional['show_all'] = true;
          }

          this.loadingSubject.next(true);
          this.eventService.list(null, 1, 50, 'deadline', 'asc', additional)
              .subscribe(result => {
                  this.eventListSubject.next(result.results);
                  if(!result.cachedVersion) {
                      this.loadingSubject.next(false);
                  }
              });
      });
  }

  public monthChanged(date) {
    if(date && date.getTime() != this.selectedCalendarMonthSubject.getValue()?.getTime()) {
      this.selectedCalendarMonthSubject.next(new Date(date));
    }
  }
  public dateChanged(date) {
    date = new Date(date);
    if(date && date.getTime() != this.selectedCalendarDateSubject.getValue()?.getTime()) {
      this.selectedCalendarDateSubject.next(new Date(date));
    }
  }

    public removeEvent(event: CooperationEventModel) {
        {
            const events = this.eventListSubject.getValue();
            const index = events.indexOf(event);
            if (index >= 0) {
                events.splice(index, 1);
                this.eventListSubject.next(events);
            }
        }

        {
            const events = this.selectedCalendarDateEventsSubject.getValue();
            const index = events.indexOf(event);
            if (index >= 0) {
                events.splice(index, 1);
                this.selectedCalendarDateEventsSubject.next(events);
            }
        }
    }

  public setMode(mode: 'list'|'calendar') {
      this.modeSubject.next(mode);
  }

    public addEvent() {
        let dialogData;
        if (this.contextBarService.getCooperationSnapshot() == null) {
            dialogData = new EventFormDialogModel({
                cooperation: {artist: null, customer: null},
                title: '',
                deadline: null,
                briefing: '',
                conditions: '',
                coupon: '',
                fee: 0,
                state: 'open',
                fullday: true
            } as CooperationEventModel);
        } else {
            dialogData = new EventFormDialogModel({
                cooperation: {id: this.contextBarService.getCooperationSnapshot().id},
                title: '',
                deadline: null,
                briefing: '',
                conditions: '',
                coupon: '',
                fee: 0,
                state: 'open',
                fullday: true
            } as CooperationEventModel);
        }


        const dialogRef = this.dialog.open(EventFormComponent, {
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(dialogResult => {
            if (dialogResult) {
                {
                    const events = this.eventListSubject.getValue();
                    events.push(dialogResult);
                    this.eventListSubject.next(events);
                }
                {
                    const events = this.selectedCalendarDateEventsSubject.getValue();
                    events.push(dialogResult);
                    this.selectedCalendarDateEventsSubject.next(events);
                }

                this.snackBar.open('Termin wurde hinzugefügt');
            }
        });
    }

    public addCooperation() {
        const dialogData = {
            cooperation: {
                artist: this.contextBarService.getArtistSnapshot(),
                events: [],
                state: 'new',
                customer: null
            }
        } as CooperationDialogModel;


        const dialogRef = this.dialog.open(CooperationDialogComponent, {
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(dialogResult => {
            if (dialogResult) {
                this.router.navigate(['/cooperations', dialogResult.id]);
                this.snackBar.open('Kooperation erstellt');
            }
        }, error => {
            console.log('error', error);
            this.snackBar.open('Fehler beim Erstellen der Kooperation');
        });
    }

    public onScroll() {
      // todo: increase page
    }
}
